<template>
  <div>
    <div v-if="isEmailReady">
      <qtm-icon-btn
        class="send-email-btn"
        color="white"
        icon="mdi-email"
        @click="showDialog"
      />
      <send-email
        :order="order"
        :replied-email="inReplyTo"
        :reply-type="replyType"
        :show="dialog"
        @close="hideDialog"
      />
      <v-expansion-panels
        v-model="openEmail"
        active-class="row-email-active"
        class="email-grid"
      >
        <v-expansion-panel disabled readonly>
          <v-expansion-panel-title>
            <v-row class="qtm-label" no-gutters>
              <v-col cols="3">
                {{ isInbound ? 'From' : 'To' }}
              </v-col>
              <v-col cols="7">
                Subject
              </v-col>
              <v-col class="text-right" cols="2">
                Date
              </v-col>
            </v-row>
          </v-expansion-panel-title>
        </v-expansion-panel>
        <v-expansion-panel
          v-for="email in emails"
          :key="email.id"
          :class="type"
          :value="email.id"
          @click="openEmailDetail(email.id)"
        >
          <v-expansion-panel-title>
            <v-row class="rows qtm-body-small" :class="email.tracking_status" no-gutters>
              <v-col class="hide-overflow" cols="3">
                <v-icon
                  v-if="canShowAlertIcon(email)"
                  color="purple"
                  size="small"
                >
                  mdi-alert
                </v-icon>
                <span>
                  {{ isInbound?email.from_address:email.to_addresses }}
                </span>
              </v-col>
              <v-col class="hide-overflow" cols="7">
                {{ email.subject }}
              </v-col>
              <v-col class="text-right text-mid-grey" cols="2">
                <v-icon v-if="email.attachment_count" size="small">
                  mdi-paperclip
                </v-icon>
                {{ dateCreated(email) }}
              </v-col>
            </v-row>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="email-detail">
            <view-email-detail
              :email-content="emailContent"
              :order="order"
              @refresh="fetchEmails"
              @reply="showReplyDialog"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
    <div v-else>
      <v-alert class="ml-auto mr-auto mt-15 elevation-10" max-width="45rem" type="info">
        This company is not email ready, please set up the company first.
      </v-alert>
    </div>
  </div>
</template>

<script setup lang="ts">
import { timeOrDateTime } from '~/models/filters'
import type { Order } from '@quotetome/materials-api'
import ViewEmailDetail from '@/components/admin/emails/view-email-detail.vue'
import SendEmail from '@/components/admin/emails/send-email.vue'

const EMAIL_REFRESH_INTERVAL = 10 * 1000 // 10 seconds

export interface Props {
  initialEmail?: number,
  order: Order,
  type: string,
  visible?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits(['update:initial-email'])

const emails = ref([])
const loading = ref(true)
const emailContent = ref({})
const dialog = ref(false)
const inReplyTo = ref()
const openEmail = ref()
const replyType = ref('single')
const refreshInterval = ref(-1)

const isEmailReady = computed(() => (props.order.owner?.company.email_ready))
const isInbound = computed(() => (props.type === 'inbound'))

watch(() => props.visible, (isVisible) => {
  if (isVisible) {
    startEmailRefresh()
  }
  else {
    stopEmailRefresh()
  }
})

const dateCreated = (email: any) => timeOrDateTime(email.date_created)

const openEmailDetail = (emailId: number) => fetchEmailContent(emailId)

const showReplyDialog = (email, type) => {
  inReplyTo.value = email
  replyType.value = type
  dialog.value = true
}

const showDialog = () => {
  inReplyTo.value = null
  dialog.value = true
}

const hideDialog = () => {
  inReplyTo.value = null
  dialog.value = false
}

const { $api, $error } = useNuxtApp()

const fetchEmailContent = async (emailId: number) => {
  loading.value = true
  try {
    emailContent.value = await $api.v1.notifications.email.detail(emailId)
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}

const fetchEmails = async () => {
  loading.value = true
  try {
    emails.value = await $api.v1.notifications.email.listByRfq(props.order, { emailType: props.type })

    if (props.initialEmail) {
      openEmail.value = props.initialEmail
      openEmailDetail(props.initialEmail)
      emit('update:initial-email', null)
    }
  }
  catch (error) {
    $error.report(error)
  }
  loading.value = false
}

const startEmailRefresh = () => {
  stopEmailRefresh()
  fetchEmails()
  refreshInterval.value = setInterval(fetchEmails, EMAIL_REFRESH_INTERVAL)
}

const stopEmailRefresh = () => {
  clearInterval(refreshInterval.value)
}

const canShowAlertIcon = (email) => ['bounced', 'rejected'].includes(email.tracking_status)

onMounted(startEmailRefresh)

onBeforeUnmount(stopEmailRefresh)
</script>
<style scoped>
  .rows {
    overflow: hidden;
  }
  .inbound .rows.delivered, .outbound .rows.rejected, .outbound .rows.bounced {
    font-weight: bold;
  }
  .hide-overflow {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .row-email-active button {
    background-color: rgb(var(--v-theme-background));
    padding: 0 10px;
  }
  .send-email-btn {
    position: absolute;
    right:  5px;
    top: 5px;
    z-index: 2;
    background-color: rgb(var(--v-theme-primary));;
  }
</style>
